import { getActiveTimezone } from '@/utils/timezone';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  Users,
  Wrench,
  Smartphone,
  AlertCircle,
  Calendar,
  Wallet,
  Download,
  Loader2,
  Receipt,
  ClipboardList,
  Clock,
  Package,
  CheckCircle,
  XCircle,
  Truck,
  AlertTriangle,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuthStore } from '@/store/authStore';
import { branchApi } from '@/services/branchApi';
import { reportApi, type ReportFilters, type DateFilter, type ServiceStatusReport, type BranchComparisonData } from '@/services/reportApi';
import { cashSettlementApi } from '@/services/cashSettlementApi';
import { getActiveCurrency } from '@/utils/currency';
import GSTR1ReportPage from './GSTR1ReportPage';
import SimpleSelect from '@/components/common/SimpleSelect';
import DatePicker from '@/components/common/DatePicker';
import { format } from 'date-fns';

type ReportType = 'services' | 'booking-person' | 'technician' | 'brand' | 'fault' | 'daily-transaction' | 'cash-settlement' | 'gstr1';
type DatePreset = 'today' | 'yesterday' | 'this-month' | 'last-month' | 'custom';

const reportTabs: { id: ReportType; label: string; icon: React.ReactNode }[] = [
  { id: 'services', label: 'Services', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'booking-person', label: 'Booking Person', icon: <Users className="w-4 h-4" /> },
  { id: 'technician', label: 'Technician', icon: <Wrench className="w-4 h-4" /> },
  { id: 'brand', label: 'Brand', icon: <Smartphone className="w-4 h-4" /> },
  { id: 'fault', label: 'Fault', icon: <AlertCircle className="w-4 h-4" /> },
  { id: 'daily-transaction', label: 'Transactions', icon: <Calendar className="w-4 h-4" /> },
  { id: 'cash-settlement', label: 'Cash Settlement', icon: <Wallet className="w-4 h-4" /> },
  { id: 'gstr1', label: 'GSTR1', icon: <Receipt className="w-4 h-4" /> },
];

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  WAITING_PARTS: 'bg-orange-100 text-orange-700',
  READY: 'bg-green-100 text-green-700',
  NOT_READY: 'bg-red-100 text-red-700',
  DELIVERED: 'bg-teal-100 text-teal-700',
};

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);

  const [activeTab, setActiveTab] = useState<ReportType>(
    (searchParams.get('type') as ReportType) || 'services'
  );

  // Date filters
  const today = new Date();
  const [datePreset, setDatePreset] = useState<DatePreset>('today');
  const [startDate, setStartDate] = useState(format(today, 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(today, 'yyyy-MM-dd'));
  const [selectedDate, setSelectedDate] = useState(format(today, 'yyyy-MM-dd'));
  const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>(
    user?.branchId || undefined
  );

  // Apply date preset
  const applyDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (preset) {
      case 'today':
        start = end = now;
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday;
        break;
      case 'this-month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'last-month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        return; // Keep current dates
    }

    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');

    setStartDate(startStr);
    setEndDate(endStr);
    setSelectedDate(startStr);
  };

  const canSelectBranch = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  // Fetch branches for filter
  const { data: branches = [] } = useQuery({
    queryKey: ['branches-list'],
    queryFn: () => branchApi.getBranchList(),
    enabled: canSelectBranch,
  });

  // Build filters based on active tab
  const getFilters = (): ReportFilters | DateFilter => {
    const branchId = selectedBranchId;

    if (activeTab === 'daily-transaction') {
      return { date: selectedDate, branchId };
    }

    return { startDate, endDate, branchId };
  };

  // Check if branchId is required for current tab - now allowing "All Branches" for cash-settlement
  const requiresBranchId = false; // Allow "All Branches" for all report types
  const hasBranchId = typeof selectedBranchId === 'string' && selectedBranchId.length > 0;

  // Fetch report data (for non-cash-settlement tabs)
  const { data: reportData, isLoading: isReportLoading, refetch: refetchReport } = useQuery({
    queryKey: ['report', activeTab, startDate, endDate, selectedDate, selectedBranchId],
    queryFn: async () => {
      const filters = getFilters();
      switch (activeTab) {
        case 'services':
          return reportApi.getServiceStatusReport(filters as ReportFilters);
        case 'booking-person':
          return reportApi.getBookingPersonReport(filters as ReportFilters);
        case 'technician':
          return reportApi.getTechnicianReport(filters as ReportFilters);
        case 'brand':
          return reportApi.getBrandReport(filters as ReportFilters);
        case 'fault':
          return reportApi.getFaultReport(filters as ReportFilters);
        case 'daily-transaction':
          return reportApi.getDailyTransactionReport(filters as DateFilter);
        default:
          return null;
      }
    },
    enabled: activeTab !== 'gstr1' && activeTab !== 'cash-settlement',
  });

  // Fetch branch comparison data for services tab (admin only)
  const { data: branchComparisonData } = useQuery({
    queryKey: ['branch-comparison', startDate, endDate],
    queryFn: () => reportApi.getBranchComparisonReport({ startDate, endDate }),
    enabled: activeTab === 'services' && canSelectBranch && !selectedBranchId,
  });

  // Fetch cash settlements list (for cash-settlement tab) - now supports "All Branches"
  const { data: settlementsData, isLoading: isSettlementsLoading, refetch: refetchSettlements } = useQuery({
    queryKey: ['cash-settlements-report', selectedBranchId || 'all', startDate, endDate],
    queryFn: () => cashSettlementApi.getAll({
      branchId: selectedBranchId, // undefined means all branches
      startDate,
      endDate,
      limit: 100,
    }),
    enabled: activeTab === 'cash-settlement',
  });

  const isLoading = activeTab === 'cash-settlement' ? isSettlementsLoading : isReportLoading;
  const refetch = activeTab === 'cash-settlement' ? refetchSettlements : refetchReport;

  const handleTabChange = (tab: ReportType) => {
    setActiveTab(tab);
    setSearchParams({ type: tab });
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!reportData) return;

    try {
      const blob = await reportApi.exportReport({
        reportType: activeTab,
        format,
        reportData,
        title: `${reportTabs.find((t) => t.id === activeTab)?.label} Report`,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(getActiveCurrency().locale, {
      style: 'currency',
      currency: getActiveCurrency().code,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const datePresets: { id: DatePreset; label: string }[] = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'this-month', label: 'This Month' },
    { id: 'last-month', label: 'Last Month' },
    { id: 'custom', label: 'Custom' },
  ];

  const renderDateFilters = () => {
    // Quick preset buttons for all report types
    const presetButtons = (
      <div className="flex items-center gap-1 flex-wrap">
        {datePresets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => applyDatePreset(preset.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              datePreset === preset.id
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    );

    if (activeTab === 'daily-transaction') {
      return (
        <div className="flex flex-col gap-3 w-full">
          {presetButtons}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Date:</label>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                wrapperClassName="w-40"
                className="!py-1.5"
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 w-full">
        {presetButtons}
        {datePreset === 'custom' && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                wrapperClassName="w-40"
                className="!py-1.5"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                wrapperClassName="w-40"
                className="!py-1.5"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSummaryCards = () => {
    if (!reportData) return null;

    if ('totals' in reportData) {
      const totals = reportData.totals as Record<string, number>;
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {Object.entries(totals).map(([key, value]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
              </p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {key.toLowerCase().includes('revenue') || key.toLowerCase().includes('amount')
                  ? formatCurrency(value)
                  : value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      );
    }

    if ('totalAmount' in reportData) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</p>
            <p className="text-xl font-bold text-green-600 mt-1">{formatCurrency(reportData.totalAmount)}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Payment Count</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{reportData.paymentCount}</p>
          </div>
          {'byMethod' in reportData && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Payment Methods</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{reportData.byMethod.length}</p>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderSummaryTable = () => {
    if (!reportData || !('summary' in reportData)) return null;

    const summary = reportData.summary as Record<string, any>[];
    if (summary.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          No data found for the selected filters
        </div>
      );
    }

    const headers = Object.keys(summary[0]).filter((k) => !k.toLowerCase().includes('id'));

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                {headers.map((header) => (
                  <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {header.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {summary.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                  {headers.map((header) => (
                    <td key={header} className="px-4 py-3 text-sm text-gray-900">
                      {header.toLowerCase().includes('time') && typeof item[header] === 'number'
                        ? `${item[header].toFixed(1)} hrs`
                        : header.toLowerCase().includes('revenue') || header.toLowerCase().includes('amount')
                        ? formatCurrency(item[header])
                        : item[header]?.toLocaleString() ?? '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPaymentMethodBreakdown = () => {
    if (!reportData || !('byMethod' in reportData)) return null;

    const byMethod = reportData.byMethod;
    if (byMethod.length === 0) return null;

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">By Payment Method</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {byMethod.map((method: any) => (
              <div
                key={method.paymentMethodId || method.methodId}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{method.paymentMethodName || method.methodName}</p>
                  <p className="text-sm text-gray-500">
                    {method.count} transaction{method.count !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-right">
                  {'openingBalance' in method ? (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Opening: {formatCurrency(method.openingBalance)}</p>
                      <p className="text-xs text-green-600">+{formatCurrency(method.receivedAmount)}</p>
                      <p className="text-sm font-bold text-gray-900">= {formatCurrency(method.closingBalance)}</p>
                    </div>
                  ) : (
                    <p className="font-bold text-green-600">{formatCurrency(method.amount)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTransactionsTable = () => {
    if (!reportData || !('transactions' in reportData)) return null;

    const transactions = reportData.transactions;
    if (transactions.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          No transactions found
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Transactions ({transactions.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map((tx: any, idx: number) => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-purple-600">{tx.ticketNumber || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{tx.customerName || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{tx.paymentMethodName}</td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                    {formatCurrency(tx.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(tx.paymentDate).toLocaleTimeString('en-IN', { hour: '2-digit',
                      minute: '2-digit', timeZone: getActiveTimezone() })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render settlements list for cash-settlement tab
  const renderSettlementsList = () => {
    if (!settlementsData?.settlements || settlementsData.settlements.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p>No settlements found for the selected period</p>
        </div>
      );
    }

    const settlements = settlementsData.settlements;

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'VERIFIED': return 'bg-green-100 text-green-700';
        case 'PENDING': return 'bg-yellow-100 text-yellow-700';
        case 'SUBMITTED': return 'bg-blue-100 text-blue-700';
        case 'REJECTED': return 'bg-red-100 text-red-700';
        default: return 'bg-gray-100 text-gray-700';
      }
    };

    // Check if viewing all branches
    const showBranchColumn = !selectedBranchId;

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">
            Settlement History ({settlements.length})
            {showBranchColumn && <span className="text-sm font-normal text-gray-500 ml-2">• All Branches</span>}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                {showBranchColumn && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Settlement ID</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Collected</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Refunds</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Out Spare</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Cash</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">UPI & Card</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Difference</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {settlements.map((settlement: any) => {
                const difference = Number(settlement.cashDifference || 0);
                // Calculate UPI & Card total from method breakdowns
                const upiCardTotal = settlement.methodBreakdowns
                  ?.filter((m: any) => {
                    const name = m.paymentMethod?.name?.toLowerCase() || '';
                    return name === 'upi' || name === 'card';
                  })
                  .reduce((sum: number, m: any) => sum + Number(m.closingBalance || 0), 0) || 0;

                return (
                  <tr key={settlement.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(settlement.settlementDate).toLocaleDateString('en-IN', { day: '2-digit',
                        month: 'short',
                        year: 'numeric', timeZone: getActiveTimezone() })}
                    </td>
                    {showBranchColumn && (
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {settlement.branch?.name || '-'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-medium text-purple-600">
                      {settlement.settlementNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                      {formatCurrency(Number(settlement.totalCollected || 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-600">
                      {formatCurrency(Number(settlement.totalRefunds || 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-orange-600">
                      {formatCurrency(Number(settlement.totalOutSpare || settlement.totalExpenses || 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                      {formatCurrency(Number(settlement.netCashAmount || 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-blue-600">
                      {formatCurrency(upiCardTotal)}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-medium ${
                      difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-500'
                    }`}>
                      {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(settlement.status)}`}>
                        {settlement.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {/* Show action info based on status */}
                      {settlement.status === 'VERIFIED' && settlement.verifiedBy && (
                        <div className="text-xs">
                          <div className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="font-medium text-gray-700">{settlement.verifiedBy.name}</span>
                          </div>
                          {settlement.verifiedAt && (
                            <span className="text-gray-500">
                              {new Date(settlement.verifiedAt).toLocaleDateString('en-IN', { day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit', timeZone: getActiveTimezone() })}
                            </span>
                          )}
                        </div>
                      )}
                      {settlement.status === 'REJECTED' && settlement.rejectedBy && (
                        <div className="text-xs">
                          <div className="flex items-center gap-1">
                            <XCircle className="w-3 h-3 text-red-500" />
                            <span className="font-medium text-gray-700">{settlement.rejectedBy.name}</span>
                          </div>
                          {settlement.rejectedAt && (
                            <span className="text-gray-500">
                              {new Date(settlement.rejectedAt).toLocaleDateString('en-IN', { day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit', timeZone: getActiveTimezone() })}
                            </span>
                          )}
                          {settlement.rejectionReason && (
                            <p className="text-gray-500 italic truncate max-w-[150px]" title={settlement.rejectionReason}>
                              {settlement.rejectionReason}
                            </p>
                          )}
                        </div>
                      )}
                      {settlement.status === 'SUBMITTED' && (
                        <span className="text-xs text-blue-600 italic">Awaiting approval</span>
                      )}
                      {settlement.status === 'PENDING' && (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render Services Status Stats Cards
  const renderServiceStatusCards = () => {
    if (activeTab !== 'services' || !reportData) return null;

    const data = reportData as ServiceStatusReport;
    const { summary, totals } = data;

    const statsCards = [
      { label: 'Total Bookings', value: totals.totalServices, icon: <ClipboardList className="w-5 h-5" />, color: 'bg-purple-500', bgColor: 'bg-purple-50' },
      { label: 'Pending', value: summary.PENDING, icon: <Clock className="w-5 h-5" />, color: 'bg-yellow-500', bgColor: 'bg-yellow-50' },
      { label: 'In Progress', value: summary.IN_PROGRESS, icon: <Wrench className="w-5 h-5" />, color: 'bg-blue-500', bgColor: 'bg-blue-50' },
      { label: 'Waiting Parts', value: summary.WAITING_PARTS, icon: <Package className="w-5 h-5" />, color: 'bg-orange-500', bgColor: 'bg-orange-50' },
      { label: 'Ready', value: summary.READY, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-green-500', bgColor: 'bg-green-50' },
      { label: 'Not Ready', value: summary.NOT_READY, icon: <XCircle className="w-5 h-5" />, color: 'bg-red-500', bgColor: 'bg-red-50' },
      { label: 'Delivered', value: summary.delivered, icon: <Truck className="w-5 h-5" />, color: 'bg-teal-500', bgColor: 'bg-teal-50' },
      { label: 'Undelivered', value: totals.totalServices - summary.delivered, icon: <AlertTriangle className="w-5 h-5" />, color: 'bg-amber-500', bgColor: 'bg-amber-50' },
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {statsCards.map((stat, idx) => (
          <div key={idx} className={`${stat.bgColor} rounded-lg border border-gray-200 p-4`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 ${stat.color} rounded-lg text-white`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render Services Status Pie Chart
  const renderServiceStatusChart = () => {
    if (activeTab !== 'services' || !reportData) return null;

    const data = reportData as ServiceStatusReport;
    const { summary } = data;

    const chartData = [
      { name: 'Pending', value: summary.PENDING, color: '#EAB308' },
      { name: 'In Progress', value: summary.IN_PROGRESS, color: '#3B82F6' },
      { name: 'Waiting Parts', value: summary.WAITING_PARTS, color: '#F97316' },
      { name: 'Ready', value: summary.READY, color: '#22C55E' },
      { name: 'Not Ready', value: summary.NOT_READY, color: '#EF4444' },
      { name: 'Delivered', value: summary.delivered, color: '#14B8A6' },
    ].filter(item => item.value > 0);

    if (chartData.length === 0) return null;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Status Distribution</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => value.toLocaleString()} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Render Branch Comparison Chart
  const renderBranchComparisonChart = () => {
    if (activeTab !== 'services' || !branchComparisonData || branchComparisonData.length === 0) return null;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Branch Comparison</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={branchComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="branchName"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={{ fontSize: 11 }}
              />
              <YAxis />
              <Tooltip
                formatter={(value: number, name: string) => [
                  name === 'revenue' ? formatCurrency(value) : value.toLocaleString(),
                  name === 'total' ? 'Total Services' : name === 'delivered' ? 'Delivered' : 'Revenue'
                ]}
              />
              <Legend />
              <Bar dataKey="total" name="Total Services" fill="#8B5CF6" />
              <Bar dataKey="delivered" name="Delivered" fill="#14B8A6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Render Branch Summary Table
  const renderBranchSummaryTable = () => {
    if (activeTab !== 'services' || !reportData) return null;

    const data = reportData as ServiceStatusReport;
    const { branchSummary } = data;

    if (!branchSummary || branchSummary.length === 0) return null;

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Branch-wise Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pending</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">In Progress</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Waiting</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ready</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Not Ready</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Delivered</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {branchSummary.map((branch, idx) => (
                <tr key={branch.branchId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{branch.branchName}</td>
                  <td className="px-4 py-3 text-sm text-center font-semibold text-purple-600">{branch.total}</td>
                  <td className="px-4 py-3 text-sm text-center text-yellow-600">{branch.pending}</td>
                  <td className="px-4 py-3 text-sm text-center text-blue-600">{branch.inProgress}</td>
                  <td className="px-4 py-3 text-sm text-center text-orange-600">{branch.waitingParts}</td>
                  <td className="px-4 py-3 text-sm text-center text-green-600">{branch.ready}</td>
                  <td className="px-4 py-3 text-sm text-center text-red-600">{branch.notReady}</td>
                  <td className="px-4 py-3 text-sm text-center text-teal-600">{branch.delivered}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(branch.revenue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-900">Total</td>
                <td className="px-4 py-3 text-sm text-center font-bold text-purple-600">
                  {branchSummary.reduce((sum, b) => sum + b.total, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-center font-semibold text-yellow-600">
                  {branchSummary.reduce((sum, b) => sum + b.pending, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-center font-semibold text-blue-600">
                  {branchSummary.reduce((sum, b) => sum + b.inProgress, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-center font-semibold text-orange-600">
                  {branchSummary.reduce((sum, b) => sum + b.waitingParts, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-center font-semibold text-green-600">
                  {branchSummary.reduce((sum, b) => sum + b.ready, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                  {branchSummary.reduce((sum, b) => sum + b.notReady, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-center font-semibold text-teal-600">
                  {branchSummary.reduce((sum, b) => sum + b.delivered, 0)}
                </td>
                <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                  {formatCurrency(branchSummary.reduce((sum, b) => sum + b.revenue, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // Render Services Details Table
  const renderServicesDetailsTable = () => {
    if (activeTab !== 'services' || !reportData) return null;

    const data = reportData as ServiceStatusReport;
    const { details } = data;

    if (!details || details.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          No services found for the selected filters
        </div>
      );
    }

    const getStatusBadge = (status: string) => {
      const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';
      return (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>
          {status.replace('_', ' ')}
        </span>
      );
    };

    const getDeliveryBadge = (deliveryStatus: string) => {
      if (deliveryStatus === 'DELIVERED') {
        return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700">Delivered</span>;
      }
      return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Pending</span>;
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Services Details ({details.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Delivery</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {details.slice(0, 100).map((service: any, idx: number) => (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-purple-600">{service.ticketNumber}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{service.customerName || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {service.deviceModel || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{service.branchName || '-'}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(service.status)}</td>
                  <td className="px-4 py-3 text-center">{getDeliveryBadge(service.deliveryStatus)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                    {formatCurrency(Number(service.actualCost || service.estimatedCost || 0))}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(service.createdAt).toLocaleDateString('en-IN', { day: '2-digit',
                      month: 'short',
                      year: 'numeric', timeZone: getActiveTimezone() })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {details.length > 100 && (
            <div className="px-4 py-3 bg-gray-50 text-center text-sm text-gray-500 border-t">
              Showing first 100 of {details.length} services. Export to view all.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 px-1 py-2 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reports</h1>
              <p className="text-xs sm:text-sm text-gray-500">Generate and export reports</p>
            </div>
          </div>

          {activeTab !== 'gstr1' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleExport('excel')}
                disabled={isLoading || !reportData}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Download className="w-4 h-4" />
                Excel
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={isLoading || !reportData}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 mb-4 sm:mb-6 overflow-x-auto">
          <div className="flex min-w-max">
            {reportTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 bg-purple-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* GSTR1 Report - Separate Component */}
        {activeTab === 'gstr1' ? (
          <GSTR1ReportPage />
        ) : (
          <>
            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1">
                    {renderDateFilters()}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {canSelectBranch && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs sm:text-sm text-gray-600">Branch:</label>
                        <SimpleSelect
                          value={selectedBranchId || ''}
                          onChange={(v) => setSelectedBranchId(v || undefined)}
                          options={[
                            { value: '', label: 'All Branches' },
                            ...branches.map((branch: any) => ({ value: branch.id, label: branch.name })),
                          ]}
                          placeholder="All Branches"
                          aria-label="Filter by branch"
                          wrapperClassName="w-40"
                          className="!py-1.5 focus:!ring-purple-500 focus:!border-purple-500"
                        />
                      </div>
                    )}
                    <button
                      onClick={() => refetch()}
                      className="px-3 sm:px-4 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs sm:text-sm font-medium whitespace-nowrap"
                    >
                      Generate Report
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Report Content */}
            {requiresBranchId && !hasBranchId ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <Wallet className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-yellow-800 mb-1">Branch Selection Required</h3>
                <p className="text-yellow-700">Please select a branch to view the Cash Settlement report.</p>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : activeTab === 'cash-settlement' ? (
              renderSettlementsList()
            ) : activeTab === 'services' ? (
              <>
                {renderServiceStatusCards()}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {renderServiceStatusChart()}
                  {canSelectBranch && !selectedBranchId && renderBranchComparisonChart()}
                </div>
                {canSelectBranch && renderBranchSummaryTable()}
                {renderServicesDetailsTable()}
              </>
            ) : (
              <>
                {renderSummaryCards()}
                {renderSummaryTable()}
                {renderPaymentMethodBreakdown()}
                {renderTransactionsTable()}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
